import { createAction, handleActions } from 'redux-actions';

import { getFirstError } from '@adjust/dashboard-core/lib/errorMessage/errorMessage';
import { GoogleSsoConfig, SsoSettings } from '@api/types';
import { Trace, TraceStatus } from '@redux/types';

/* eslint-disable @typescript-eslint/naming-convention */
const getParsingErrorMessages = () => ({
  malformed_xml: t('sso.invalid-file.error.dialog'),
  invalid_certificate: t('sso.invalid-file-content.error.dialog', { errorReason: 'invalid_certificate' }),
  missing_sso_tag: t('sso.invalid-file-content.error.dialog', { errorReason: 'missing_sso_tag' }),
  invalid_idp_identity_id: t('sso.invalid-file-content.error.dialog', { errorReason: 'invalid_idp_identity_id' }),
  invalid_sso_target: t('sso.invalid-file-content.error.dialog', { errorReason: 'invalid_sso_target' }),
});
/* eslint-enable @typescript-eslint/naming-convention */

const getSettingsErrorMessages = (errors?: string[]) => {
  if (!errors || !errors.length) {
    return t('sso.credentials.error.dialog');
  }

  return getFirstError(errors, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    has_already_been_taken: t('sso.file-already-used.error'),
  });
};

interface GoogleSso {
  isLoading: boolean;
  isSubmitting: boolean;
  isParsing: boolean;
  config: Nullable<GoogleSsoConfig>;
  parsedSettings: Nullable<SsoSettings>;
  isAlertOpen: boolean;
  trace: Trace;
}

export const initialState: GoogleSso = {
  isLoading: false,
  isSubmitting: false,
  isParsing: false,
  config: null,
  parsedSettings: null,
  isAlertOpen: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
};

// Actions

export const DATA_FETCH_STARTED = 'ssoConfiguration/googleSso/DATA_FETCH_STARTED';
export const DATA_FETCH_SUCCEEDED = 'ssoConfiguration/googleSso/DATA_FETCH_SUCCEEDED';
export const DATA_CREATE_STARTED = 'ssoConfiguration/googleSso/DATA_CREATE_STARTED';
export const DATA_CREATE_FAILED = 'ssoConfiguration/googleSso/DATA_CREATE_FAILED';
export const DATA_CREATE_SUCCEEDED = 'ssoConfiguration/googleSso/DATA_CREATE_SUCCEEDED';
export const DATA_UPDATE_STARTED = 'ssoConfiguration/googleSso/DATA_UPDATE_STARTED';
export const DATA_UPDATE_FAILED = 'ssoConfiguration/googleSso/DATA_UPDATE_FAILED';
export const DATA_UPDATE_SUCCEEDED = 'ssoConfiguration/googleSso/DATA_UPDATE_SUCCEEDED';
export const SETTINGS_PARSING_STARTED = 'ssoConfiguration/googleSso/SETTINGS_PARSING_STARTED';
export const SETTINGS_PARSING_SUCCEEDED = 'ssoConfiguration/googleSso/SETTINGS_PARSING_SUCCEEDED';
export const SETTINGS_PARSING_FAILED = 'ssoConfiguration/googleSso/SETTINGS_PARSING_FAILED';
export const SETTINGS_CLEAR = 'ssoConfiguration/googleSso/SETTINGS_CLEAR';
export const ALERT_CLOSE = 'ssoConfiguration/googleSso/ALERT_CLOSE';

// Action creators

export const fetchData = createAction(DATA_FETCH_STARTED);
export const fetchDataSucceeded = createAction(DATA_FETCH_SUCCEEDED);
export const createData = createAction(DATA_CREATE_STARTED);
export const createDataFailed = createAction(DATA_CREATE_FAILED);
export const createDataSucceeded = createAction(DATA_CREATE_SUCCEEDED);
export const updateData = createAction(DATA_UPDATE_STARTED);
export const updateDataFailed = createAction(DATA_UPDATE_FAILED);
export const updateDataSucceeded = createAction(DATA_UPDATE_SUCCEEDED);
export const parseSettings = createAction(SETTINGS_PARSING_STARTED);
export const parseSettingsFailed = createAction(SETTINGS_PARSING_FAILED);
export const parseSettingsSucceeded = createAction(SETTINGS_PARSING_SUCCEEDED);
export const clearSettings = createAction(SETTINGS_CLEAR);
export const closeAlert = createAction(ALERT_CLOSE);

// Reducer

const handleFetchStarted = (state: GoogleSso): GoogleSso => ({
  ...state,
  isLoading: true,
});

const handleFetchSucceeded = (state: GoogleSso, { payload }): GoogleSso => ({
  ...state,
  isLoading: false,
  config: payload,
});

const handleCreateStarted = (state: GoogleSso): GoogleSso => ({
  ...state,
  isSubmitting: true,
});

const handleCreateFailed = (state: GoogleSso, { payload }): GoogleSso => ({
  ...state,
  isSubmitting: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: getSettingsErrorMessages(payload.errors?.settings),
  },
});

const handleCreateSucceeded = (state: GoogleSso): GoogleSso => ({
  ...state,
  isSubmitting: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: t('sso.credentials.accept.dialog'),
  },
});

const handleUpdateStarted = (state: GoogleSso): GoogleSso => ({
  ...state,
  isSubmitting: true,
});

const handleUpdateFailed = (state: GoogleSso, { payload }): GoogleSso => ({
  ...state,
  isSubmitting: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: getSettingsErrorMessages(payload.errors?.settings),
  },
});

const handleUpdateSucceeded = (state: GoogleSso): GoogleSso => ({
  ...state,
  isSubmitting: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Info,
    message: t('sso.credentials.accept.dialog'),
  },
});

const handleParsingStarted = (state: GoogleSso): GoogleSso => ({
  ...state,
  isParsing: true,
  parsedSettings: null,
});

const handleParsingFailed = (state: GoogleSso, { payload }): GoogleSso => ({
  ...state,
  isParsing: false,
  isAlertOpen: true,
  trace: {
    status: TraceStatus.Error,
    message: getFirstError(payload.errors?.file, getParsingErrorMessages()),
  },
});

const handleParsingSucceeded = (state: GoogleSso, { payload }): GoogleSso => ({
  ...state,
  isParsing: false,
  parsedSettings: payload,
});

const handleSettingsClear = (state: GoogleSso): GoogleSso => ({
  ...state,
  parsedSettings: null,
});

const handleAlertClose = (state: GoogleSso): GoogleSso => ({
  ...state,
  isAlertOpen: false,
});

export default handleActions(
  {
    [DATA_FETCH_STARTED]: handleFetchStarted,
    [DATA_FETCH_SUCCEEDED]: handleFetchSucceeded,
    [DATA_CREATE_STARTED]: handleCreateStarted,
    [DATA_CREATE_FAILED]: handleCreateFailed,
    [DATA_CREATE_SUCCEEDED]: handleCreateSucceeded,
    [DATA_UPDATE_STARTED]: handleUpdateStarted,
    [DATA_UPDATE_FAILED]: handleUpdateFailed,
    [DATA_UPDATE_SUCCEEDED]: handleUpdateSucceeded,
    [SETTINGS_PARSING_STARTED]: handleParsingStarted,
    [SETTINGS_PARSING_FAILED]: handleParsingFailed,
    [SETTINGS_PARSING_SUCCEEDED]: handleParsingSucceeded,
    [SETTINGS_CLEAR]: handleSettingsClear,
    [ALERT_CLOSE]: handleAlertClose,
  },
  initialState,
);

// Selectors

export const getGoogleSsoConfig = (state): GoogleSso => state.ssoConfiguration.googleSso;
